// Browser-only recovery; this is never an authoritative exam submission.
const VERSION = 1;
const PREFIX = "liontheory:exam-draft:v1:";
const isObject = value => value !== null && typeof value === "object" && !Array.isArray(value);

export function getDraftIdentity(session, examId) {
  if (!session?.active || !session.draftSessionId || !session.tmtaId ||
      !session.examType || session.examId !== examId ||
      !Number.isFinite(Date.parse(session.expiresAt))) return null;

  const scope = JSON.stringify([
    String(session.tmtaId), session.examType, examId, session.draftSessionId
  ]);
  return { key: PREFIX + scope, scope, expiresAt: Date.parse(session.expiresAt) };
}

// Restore responses and listen counts, but never autoplay/restart a recording
// that was playing when the page refreshed. A used listen remains used.
function restoreValue(value) {
  if (Array.isArray(value)) return value.map(restoreValue);
  if (!isObject(value)) return value;
  const restored = Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, restoreValue(item)])
  );
  if (typeof value.listensUsed === "number" && typeof value.playing === "boolean") {
    restored.playing = false;
    restored.currentTime = 0;
  }
  return restored;
}

export function readExamDraft(identity, storage, now = Date.now()) {
  if (!identity) return { answers: null, error: null };
  try {
    const raw = storage.getItem(identity.key);
    if (!raw) return { answers: null, error: null };
    const draft = JSON.parse(raw);
    if (draft.version !== VERSION || draft.scope !== identity.scope ||
        draft.expiresAt <= now || identity.expiresAt <= now ||
        !Number.isFinite(draft.expiresAt) || !isObject(draft.answers)) {
      return { answers: null, error: "Saved work could not be restored for this exam session." };
    }
    return { answers: restoreValue(draft.answers), error: null };
  } catch {
    return { answers: null, error: "Saved work could not be read from this browser." };
  }
}

export function writeExamDraft(identity, answers, storage) {
  if (!identity) return false;
  try {
    storage.setItem(identity.key, JSON.stringify({
      version: VERSION,
      scope: identity.scope,
      expiresAt: identity.expiresAt,
      savedAt: Date.now(),
      answers
    }));
    return true;
  } catch {
    // Quota limits, blocked storage, and serialization failures cannot stop input.
    return false;
  }
}

export function removeExamDraft(identity, storage) {
  if (!identity) return;
  try { storage.removeItem(identity.key); } catch { /* Submission already succeeded. */ }
}
